/**
 * F7 (Jun-2026, User-Wunsch) — Verkäufer-Dashboard „Meine Provisionen".
 *
 * Zeigt erwirtschaftete, bestätigte/abrufbare und bereits angeforderte
 * Provisionen. Der Verkäufer kann einen abrufbaren Betrag pro Vorgang „abrufen"
 * (für die nächste Abrechnung anfordern) — der Rest bleibt angespart.
 */
import { useState, useEffect, useCallback } from "react";
import { Percent, Loader2, CheckCircle2, Wallet, Send, FileText, Target, Euro } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import CommissionInfoCard from "../vermittler/CommissionInfoCard";

function _curMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const PAYOUT_LABELS = {
    offen: "Bestätigt – abrufbar",
    angefordert: "Angefordert",
    abgerechnet: "Abgerechnet",
    ausgezahlt: "Ausgezahlt",
};
const PAYOUT_CLS = {
    offen: "bg-emerald-100 text-emerald-800",
    angefordert: "bg-amber-100 text-amber-800",
    abgerechnet: "bg-blue-100 text-blue-800",
    ausgezahlt: "bg-emerald-200 text-emerald-900",
};

function Stat({ label, value, cls = "text-[#111827]", testid }) {
    return (
        <div className="bg-white border border-gray-200 rounded-sm px-3 py-2.5" data-testid={testid}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</div>
            <div className={`text-lg font-black font-mono-num ${cls}`}>{value}</div>
        </div>
    );
}

export default function SellerCommissionsCard({ dealerId } = {}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [amounts, setAmounts] = useState({});
    const [month, setMonth] = useState(_curMonth());
    const [pdfBusy, setPdfBusy] = useState(false);

    const downloadAbrechnung = async () => {
        setPdfBusy(true);
        try {
            const res = await api.get("/me/seller-commissions/abrechnung.pdf", {
                params: { month }, responseType: "blob",
            });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `meine_provisionsabrechnung_${month}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error("Abrechnung konnte nicht erstellt werden");
        } finally {
            setPdfBusy(false);
        }
    };

    const load = useCallback(async () => {
        try {
            const r = await api.get("/me/seller-commissions", {
                params: dealerId ? { dealer_id: dealerId } : {},
            });
            setData(r.data);
        } catch {
            /* still */
        } finally {
            setLoading(false);
        }
    }, [dealerId]);

    useEffect(() => { load(); }, [load]);

    const requestPayout = async (lid, fallback) => {
        setBusy(lid);
        try {
            const raw = amounts[lid];
            const amount = raw === undefined || raw === "" ? fallback : Number(raw);
            await api.post(`/me/seller-commissions/${lid}/request-payout`, { amount });
            toast.success("Provision für die nächste Abrechnung angefordert");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Abruf fehlgeschlagen");
        } finally {
            setBusy("");
        }
    };

    if (loading) {
        return <div className="py-8 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Lädt …</div>;
    }
    const s = data?.summary || {};
    const items = data?.items || [];
    if (items.length === 0 && (s.earned || 0) === 0 && (s.month_target || 0) === 0) {
        return (
            <div data-testid="seller-commissions-empty-wrap">
                <div className="bg-white border border-gray-200 rounded-sm p-6 text-center text-gray-500" data-testid="seller-commissions-empty">
                    <Percent className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    Noch keine Provisionen erfasst. Sobald dir Verkäufe zugeordnet sind, erscheinen hier deine erwirtschafteten und abrufbaren Provisionen.
                </div>
                <CommissionInfoCard dealerId={dealerId} embedded />
            </div>
        );
    }

    return (
        <div data-testid="seller-commissions-card">
            <div className="flex flex-wrap items-center justify-end gap-2 mb-3">
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mr-auto">Provisions-Abrechnung</span>
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                    className="h-9 w-40 text-sm" data-testid="seller-commission-month" />
                <Button size="sm" onClick={downloadAbrechnung} disabled={pdfBusy}
                    variant="outline" className="h-9 rounded-sm text-xs font-bold" data-testid="seller-commission-pdf">
                    {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1" /> Abrechnung als PDF</>}
                </Button>
            </div>

            {/* EUR-Provision (im Dienst, laufender Monat) — aus dem Privat-Verdienst
                hierher (Firma) verschoben, da EUR-Provision je Arbeitgeber entsteht. */}
            <div className="bg-gradient-to-br from-[#111827] to-[#1f2937] text-white border-2 border-[#FFCC00] rounded-sm p-4 mb-4" data-testid="seller-commission-eur">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-gray-300 mb-1">
                    <Euro className="w-3.5 h-3.5 text-emerald-400" /> EUR-Provision · {s.month || ""}
                </div>
                <div className="font-display font-black text-3xl font-mono-num leading-none text-emerald-400" data-testid="seller-commission-eur-value">
                    {formatPrice(s.month_confirmed || 0)}
                </div>
                <div className="text-[11px] text-gray-400 mt-1.5" data-testid="seller-commission-eur-sales">
                    aus {s.month_sales || 0} {(s.month_sales || 0) === 1 ? "Verkauf" : "Verkäufen"} · abgerechnet
                </div>
                {(s.available || 0) > 0 && (
                    <div className="text-[11px] text-amber-300 mt-0.5" data-testid="seller-commission-eur-pending">
                        + {formatPrice(s.available || 0)} abrufbar (noch nicht ausgezahlt)
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Stat label="Erwirtschaftet" value={formatPrice(s.earned || 0)} testid="seller-commission-earned" />
                <Stat label="Bestätigt" value={formatPrice(s.confirmed || 0)} cls="text-emerald-700" testid="seller-commission-confirmed" />
                <Stat label="Abrufbar" value={formatPrice(s.available || 0)} cls="text-indigo-700" testid="seller-commission-available" />
                <Stat label="Angefordert" value={formatPrice(s.requested || 0)} cls="text-amber-700" testid="seller-commission-requested" />
            </div>

            {(s.month_target || 0) > 0 && (
                <div className="bg-white border border-gray-200 rounded-sm px-4 py-3 mb-4" data-testid="seller-commission-target">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                            <Target className="w-3.5 h-3.5 text-[#FFCC00]" /> Monatsziel (Umsatz)
                        </span>
                        <span className="text-xs font-mono-num font-bold text-gray-700" data-testid="seller-commission-target-values">
                            {formatPrice(s.month_revenue_net || 0)} / {formatPrice(s.month_target || 0)}
                        </span>
                    </div>
                    <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all ${(s.month_target_pct || 0) >= 100 ? "bg-emerald-500" : "bg-[#FFCC00]"}`}
                            style={{ width: `${Math.min(100, s.month_target_pct || 0)}%` }}
                            data-testid="seller-commission-target-bar"
                        />
                    </div>
                    <div className="mt-1 text-[11px] text-gray-500 flex items-center justify-between">
                        <span data-testid="seller-commission-target-pct">
                            {(s.month_target_pct ?? 0)}% erreicht
                        </span>
                        {(s.month_target_pct || 0) >= 100
                            ? <span className="text-emerald-700 font-bold">Ziel erreicht 🎉</span>
                            : <span>noch {formatPrice(Math.max(0, (s.month_target || 0) - (s.month_revenue_net || 0)))} Umsatz</span>}
                    </div>
                </div>
            )}

            <div className="space-y-2">
                {items.length === 0 && (
                    <div className="text-center text-sm text-gray-500 py-4" data-testid="seller-commission-items-empty">
                        Noch keine Provisionen diesen Monat — bleib dran, dein Ziel ist in Reichweite!
                    </div>
                )}
                {items.map((it) => {
                    const canRequest = it.payout_status === "offen" && (it.confirmed_total || 0) > 0;
                    return (
                        <div key={it.listing_id} className="bg-white border border-gray-200 rounded-sm p-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`seller-commission-item-${it.listing_id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-gray-800 text-sm">{it.brand} {it.model || it.listing_title}</div>
                                <div className="text-[11px] text-gray-500">
                                    Fahrzeug {formatPrice(it.vehicle_amount || 0)} · Zusatzertrag {formatPrice(it.extra_amount || 0)}
                                    {" · "}
                                    <span className="text-gray-700 font-bold">{formatPrice(it.confirmed_total || 0)} bestätigt</span>
                                </div>
                            </div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-black uppercase shrink-0 ${PAYOUT_CLS[it.payout_status] || PAYOUT_CLS.offen}`} data-testid={`seller-commission-status-${it.listing_id}`}>
                                {it.payout_status === "ausgezahlt" && <CheckCircle2 className="w-3 h-3" />}
                                {PAYOUT_LABELS[it.payout_status] || it.payout_status}
                            </span>
                            {canRequest ? (
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <Input
                                        type="number" min="0" step="0.01"
                                        value={amounts[it.listing_id] ?? it.confirmed_total}
                                        onChange={(e) => setAmounts((p) => ({ ...p, [it.listing_id]: e.target.value }))}
                                        className="w-24 h-9 text-sm font-mono-num"
                                        data-testid={`seller-commission-amount-${it.listing_id}`}
                                    />
                                    <Button
                                        size="sm"
                                        onClick={() => requestPayout(it.listing_id, it.confirmed_total)}
                                        disabled={busy === it.listing_id}
                                        className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                                        data-testid={`seller-commission-request-${it.listing_id}`}
                                    >
                                        {busy === it.listing_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Abrufen</>}
                                    </Button>
                                </div>
                            ) : it.payout_status === "angefordert" ? (
                                <span className="text-[11px] text-amber-700 font-bold shrink-0 flex items-center gap-1" data-testid={`seller-commission-requested-amount-${it.listing_id}`}>
                                    <Wallet className="w-3.5 h-3.5" /> {formatPrice(it.requested_amount || 0)} angefordert
                                </span>
                            ) : null}
                        </div>
                    );
                })}
            </div>
            {items.length > 0 && (
                <p className="text-[10px] text-gray-500 mt-2">
                    Du kannst auch weniger als den abrufbaren Betrag anfordern — der Rest bleibt für die Nebensaison angespart.
                </p>
            )}
            <CommissionInfoCard dealerId={dealerId} embedded />
        </div>
    );
}
